import { useEffect, useState, type ReactNode } from 'react';

import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';
import { parseLiveInputStatusUpdateMessage } from '@/services/inputStatus/live-input-status.service';

export type InputLiveStatusSnapshot = {
  inputId: string;
  overallStatus?: string;
  liveProgramIds: string[];
};

type LiveStatusListener = (status: InputLiveStatusSnapshot) => void;

const statusByInputId = new Map<string, InputLiveStatusSnapshot>();
const listenersByInputId = new Map<string, Set<LiveStatusListener>>();

const readOverallStatus = (data?: Record<string, unknown> | null): string | undefined => {
  if (!data) {
    return undefined;
  }

  return (data.overallStatus ?? data.OverallStatus ?? data.status) as string | undefined;
};

const readLiveProgramIds = (data?: Record<string, unknown> | null): string[] => {
  if (!data || !Array.isArray(data.liveProgramIds)) {
    return [];
  }

  return data.liveProgramIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
};

const notifyListeners = (status: InputLiveStatusSnapshot) => {
  statusByInputId.set(status.inputId, status);
  listenersByInputId.get(status.inputId)?.forEach((listener) => listener(status));
};

export const fetchInputLiveStatusSnapshot = async (inputId: string): Promise<InputLiveStatusSnapshot> => {
  const response = (await FuelToolsApiService.getInputStreamStatus(inputId)) as Record<string, unknown> | null;
  const snapshot: InputLiveStatusSnapshot = {
    inputId,
    overallStatus: readOverallStatus(response ?? undefined),
    liveProgramIds: readLiveProgramIds(response ?? undefined),
  };

  notifyListeners(snapshot);
  return snapshot;
};

const subscribeToInputLiveStatus = (inputId: string, listener: LiveStatusListener) => {
  if (!listenersByInputId.has(inputId)) {
    listenersByInputId.set(inputId, new Set());
  }

  listenersByInputId.get(inputId)?.add(listener);

  const current = statusByInputId.get(inputId);
  if (current) {
    listener(current);
  }

  return () => {
    listenersByInputId.get(inputId)?.delete(listener);
  };
};

/** Mount once per live-broadcast page to listen for WebSocket status notifications. */
export const InputSourceLiveStatusProvider = ({ children }: { children: ReactNode }) => {
  const { lastJsonMessage } = useWebSocketContext();

  useEffect(() => {
    if (lastJsonMessage == null) {
      return;
    }

    const payload = parseLiveInputStatusUpdateMessage(lastJsonMessage);
    if (!payload?.inputId) {
      return;
    }

    void fetchInputLiveStatusSnapshot(payload.inputId).catch((error) => {
      console.error('Failed to refresh input live status from WebSocket', { inputId: payload.inputId, error });
    });
  }, [lastJsonMessage]);

  return children;
};

export const useInputSourceLiveStatus = (inputId?: string) => {
  const [liveStatus, setLiveStatus] = useState<InputLiveStatusSnapshot | null>(() =>
    inputId ? statusByInputId.get(inputId) ?? null : null
  );

  useEffect(() => {
    if (!inputId) {
      setLiveStatus(null);
      return undefined;
    }

    return subscribeToInputLiveStatus(inputId, setLiveStatus);
  }, [inputId]);

  useEffect(() => {
    if (!inputId) {
      return undefined;
    }

    void fetchInputLiveStatusSnapshot(inputId).catch((error) => {
      console.error('Failed to fetch input live status', { inputId, error });
    });
  }, [inputId]);

  return {
    liveStatus,
    subscribe: (listener: LiveStatusListener) => {
      if (!inputId) {
        return () => undefined;
      }

      return subscribeToInputLiveStatus(inputId, listener);
    },
  };
};
